'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { ArrowLeft, CalendarDays, Mic, Shield } from 'lucide-react';
import {
  SectLabel,
  SectLede,
  V4GhostButton,
  GlassCard,
} from '@/components/landing/v4-shared';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { PricingCustomJsonLd } from '@/components/pricing/pricing-custom-json-ld';
import { PRICING_CUSTOM_COPY } from '@/lib/marketing/messaging-bank';
import { ELEVENLABS_AGENT_IDS } from '@config/elevenlabs';
import '@/components/pricing/pricing.v4.css';

const FloatingVoiceV1 = dynamic(() => import('@/components/voice/FloatingVoiceV1'), {
  ssr: false,
});

const CUSTOM_SALES_AGENT_ID =
  process.env.NEXT_PUBLIC_ELEVENLABS_CUSTOM_SALES_AGENT_ID?.trim() ||
  ELEVENLABS_AGENT_IDS.customSales;

const italicAccent = {
  fontFamily: 'var(--font-instrument)',
  fontStyle: 'italic',
  fontWeight: 400,
  color: 'var(--cyan-glow)',
} as const;

const INFO_CARDS = [
  {
    icon: <Mic size={18} aria-hidden="true" style={{ color: 'var(--cyan-glow)' }} />,
    title: 'Tell Bri your needs',
    desc: 'Describe the exact business processes you want to automate.',
  },
  {
    icon: <Shield size={18} aria-hidden="true" style={{ color: 'var(--cyan-glow)' }} />,
    title: 'Custom pricing',
    desc: 'Pricing tailored to your automation scope — no one-size-fits-all.',
  },
  {
    icon: <CalendarDays size={18} aria-hidden="true" style={{ color: 'var(--cyan-glow)' }} />,
    title: 'Schedule follow-up',
    desc: 'Book a call with our team to finalize your custom plan.',
  },
];

export function CustomSalesClient() {
  return (
    <main
      data-v4-root
      data-pillar="cyan"
      style={{
        background: 'var(--ink)',
        color: 'var(--paper)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
      className="relative"
    >
      <PricingCustomJsonLd />
      <Navbar />

      {/* Back nav */}
      <div className="v4-section" style={{ paddingTop: 110, paddingBottom: 0 }}>
        <Link
          href="/pricing"
          className="v4-pr-link"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: 'var(--font-geist-mono)',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            color: 'var(--cyan-glow)',
            textDecoration: 'none',
          }}
        >
          <ArrowLeft size={12} aria-hidden="true" /> Back to pricing
        </Link>
      </div>

      {/* Hero */}
      <section
        className="v4-pr-hero v4-section v4-pr-speakable speakable-summary"
        aria-labelledby="custom-h1"
        style={{ paddingTop: 36, paddingBottom: 24, textAlign: 'center' }}
      >
        <div style={{ maxWidth: 820, margin: '0 auto' }}>
          <SectLabel color="var(--cyan-glow)">Custom enterprise plan</SectLabel>
          <h1
            id="custom-h1"
            style={{
              fontFamily: 'var(--font-geist-sans)',
              fontWeight: 500,
              fontSize: 'clamp(36px, 6vw, 80px)',
              letterSpacing: '-0.035em',
              lineHeight: 1.05,
              color: 'var(--paper)',
              maxWidth: '20ch',
              margin: '0 auto 22px',
            }}
          >
            Let’s build your <em style={italicAccent}>custom automation</em>.
          </h1>
          <SectLede style={{ margin: '0 auto 28px' }}>
            {PRICING_CUSTOM_COPY.lede}
          </SectLede>
        </div>
      </section>

      {/* Voice widget */}
      <section
        className="v4-section"
        aria-labelledby="custom-voice"
        style={{ paddingTop: 8, paddingBottom: 32, flex: 1 }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <GlassCard accent="cyan" hoverable padding={32}>
            <h2
              id="custom-voice"
              style={{
                fontFamily: 'var(--font-geist-mono)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.24em',
                color: 'var(--cyan-glow)',
                margin: '0 0 12px',
                textAlign: 'center',
              }}
            >
              Talk to Bri
            </h2>
            <p
              style={{
                fontFamily: 'var(--font-geist-sans)',
                fontSize: 14,
                lineHeight: 1.55,
                color: 'var(--dim)',
                margin: '0 0 24px',
                textAlign: 'center',
              }}
            >
              Click the mic below to start your conversation.
            </p>
            <div
              style={{ display: 'flex', justifyContent: 'center' }}
              data-testid="custom-sales-widget"
            >
              <FloatingVoiceV1
                surface="custom_pricing"
                variant="centered-ring"
                agentId={CUSTOM_SALES_AGENT_ID}
                helperText={{
                  idle: 'Click the mic below to start your conversation.',
                  connecting: 'Connecting Bri to the custom automation prompt.',
                  listening: 'Bri is listening. Tell her what you want to automate.',
                  speaking: 'Bri is talking through your options.',
                  ended: 'Call ended. Tap to talk again.',
                }}
                postCall={{ mode: 'restart' }}
              />
            </div>
          </GlassCard>
        </div>
      </section>

      {/* Info cards */}
      <section className="v4-section" aria-labelledby="custom-process" style={{ paddingTop: 8, paddingBottom: 32 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2
            id="custom-process"
            style={{
              fontFamily: 'var(--font-geist-mono)',
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.24em',
              color: 'var(--dim)',
              margin: '0 0 20px',
              textAlign: 'center',
            }}
          >
            How is a custom BizRnR plan scoped?
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 16,
            }}
          >
            {INFO_CARDS.map((card) => (
              <GlassCard key={card.title} accent="cyan" hoverable>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: 10,
                  }}
                >
                  {card.icon}
                </div>
                <h3
                  style={{
                    fontFamily: 'var(--font-geist-sans)',
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'var(--paper)',
                    margin: '0 0 6px',
                    textAlign: 'center',
                  }}
                >
                  {card.title}
                </h3>
                <p
                  style={{
                    fontFamily: 'var(--font-geist-sans)',
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    color: 'var(--dim)',
                    margin: 0,
                    textAlign: 'center',
                  }}
                >
                  {card.desc}
                </p>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Cal.com fallback */}
      <section
        className="v4-section"
        style={{ paddingTop: 8, paddingBottom: 64, textAlign: 'center' }}
      >
        <p
          style={{
            fontFamily: 'var(--font-geist-mono)',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            color: 'var(--dim)',
            margin: '0 0 12px',
          }}
        >
          Prefer to schedule directly?
        </p>
        <V4GhostButton
          href="https://cal.com/bizrnr"
          variant="cyan"
          className="v4-pr-cta"
        >
          <CalendarDays size={14} aria-hidden="true" /> Book on Cal.com
        </V4GhostButton>
      </section>

      <Footer />
    </main>
  );
}
